import { DossierStoreProps } from '@/app/[locale]/(root)/(MC)/dossier/(processing-detail)/_components/utils/utils';
import { parseDynamicFormConfigData } from '@/constant/dynamic-form-constants';
import { AssignTaskDetailResponse } from '@/server/api/v1/dossier-assign-task/type';
import { DossierFeeResponse } from '@/server/api/v1/dossier-fee/type';
import { DossierNotificationResponse } from '@/server/api/v1/dossier-notification/type';
import { DossierPauseResponse } from '@/server/api/v1/dossier-pause/type';
import {
    DossierDetailProcessResponse,
    DossierDetailResponse,
    DossierSummaryProcessesResponse,
} from '@/server/api/v1/dossier/type';
import {
    ImplementationProcess,
    ImplementationStep,
    ProcedureResponse,
} from '@/server/api/v1/procedure/type';
import {
    AssignTaskStatus,
    DossierButtonAction,
    DossierCondition,
    DossierDetailProcessingStatus,
    DossierPauseStatus,
    DossierPauseType,
    DossierPostalInformationStatus,
    DossierStatus,
    DossierSummaryProcessStatus,
    ForwardType,
    SummaryPauseStatus,
} from '@/types/enum';
import deepmerge from 'deepmerge';
import { dynamicSort } from '../../../../../../lib/random';
import { nameof, sortByNumberNullable } from '../../../../../../lib/utils';

type Props = {
    /**Thông tin hồ sơ */
    dossier?: DossierDetailResponse;
    /**ID nhân viên xử lý */
    employeeId?: string;
    /**UnitCode của nhân viên xử lý */
    unitCode?: string;
    /**DepartmentCode của nhân viên xử lý */
    departmentCode?: string;

    /**
     * Có kiểm tra điều kiện các nút không?
     * @default true
     */
    checkBtnState?: boolean;

    /**
     * Có kiểm tra giao việc không?
     * @default true
     */
    checkAssignTask?: boolean;
};

/**
 * Hàm tổng hợp các thông tin cần thiết cho việc xử lý hồ sơ.
 *
 * Có thể sử dụng ở cả 2 side(Client và Server)
 */
function dossierDetailHandler({
    dossier,
    employeeId,
    unitCode,

    checkBtnState = true,
    checkAssignTask = true,
}: Props) {
    /**Danh sách các đơn vị xử lý */
    let dossierSummaryProcess: DossierSummaryProcessesResponse[] = [];
    /**Đây là luồng xử lý được lấy theo đơn vị của nhân viên xử lý (chỉ có khi hồ sơ đã được tiếp nhận)*/
    let currentSummaryProcess: DossierSummaryProcessesResponse | undefined;
    /**Đây là luồng xử lý chính của hồ sơ (Type = Main) */
    let mainSummaryProcess: DossierSummaryProcessesResponse | undefined;

    /**Danh sách các bước thực hiện xử lý hồ sơ này (Đã sort theo Ordinal tăng dần) */
    let implementSteps: ImplementationStep[] = [];

    /**Danh sách các quy trình xử lý hồ sơ này */
    let implementProcesses: ImplementationProcess[] = [];

    let startImplementStepCode: string | undefined;

    let endImplementStepCode: string | undefined;

    /**Mã quy trình xử lý hiện tại */
    let implementProcessCode: string | undefined;

    /**Thông tin xử lý mới nhất của nhân viên đang đăng nhập */
    let currentProcessStep: DossierDetailProcessResponse | undefined;
    /**Thông tin xử lý mới nhất của hồ sơ */
    let latestProcessStep: DossierDetailProcessResponse | undefined;

    /**Bước xử lý hiện tại của hồ sơ */
    let currentImplementStep: ImplementationStep | undefined;
    /**Bước xử lý trước bước hiện tại của hồ sơ */
    let beforeImplementStep: ImplementationStep | undefined;
    /**Bước xử lý sau bước hiện tại của hồ sơ */
    let nextImplementStep: ImplementationStep | undefined;

    /**Danh sách giao việc của hồ sơ */
    let assignTasks: AssignTaskDetailResponse[] = [];
    /**Chi tiết giao việc hiện tại của nhân viên nếu có (Đang xử lý) */
    let currentAssignTask: AssignTaskDetailResponse | undefined;

    /**Danh sách dừng tính của hồ sơ */
    let dossierPauses: DossierPauseResponse[] = [];

    /**Danh sách phí của hồ sơ */
    let dossierFees: DossierFeeResponse[] = [];

    /**Node khởi tạo hay node tiếp nhận ban đầu của hồ sơ */
    let initialNodeReceive: DossierDetailProcessResponse | undefined;

    /** Trạng thái edit hồ sơ */
    let isEdit = !!dossier && dossier.Status !== DossierStatus.WaittingReceive;

    /** Đang chờ bổ sung hồ sơ */
    let isAdditionalRequest = false;

    /**Đây là hồ sơ nộp online chờ tiếp nhận */
    let isWaitingReceiveOnline = !!(
        dossier && dossier.Status === DossierStatus.WaittingReceive
    );

    /**Trạng thái chỉ xem hồ sơ */
    let isReadOnly =
        dossier?.Status &&
        [
            DossierStatus.WaitingForReturn,
            DossierStatus.NotResolve,
            DossierStatus.Returned,
        ].includes(dossier?.Status);
    // Điều kiện hiển thị các nút --------'

    /**Nút tiếp nhận mới hồ sơ */
    let isShowBtnCreateDossier = false;
    /**Nút tiếp nhận hồ sơ hoàn thành */
    let isShowBtnReceiveCompleted = false;
    /**Nút lưu hồ sơ khi sửa */
    let isShowBtnEdit = false;
    /**Nút in biên nhận */
    let isShowBtnPrintReceipt = false;
    /**Nút hủy hồ sơ */
    let isShowBtnCancelDossier = false;
    /**Nút tiếp nhận bổ sung hồ sơ */
    let isShowBtnReceiveAdditional = false;

    /**Nút thu hồi */
    let isShowBtnRevoke = false;
    /**Nút duyệt hồ sơ */
    let isShowBtnApproveDossier = false;
    /**Nút chuyển bước trước */
    let isShowBtnForwardPrev = false;
    /**Nút chuyển bước tiếp theo */
    let isShowBtnForwardNext = false;
    /**Nút trả kết quả */
    let isShowBtnReturnResult = false;
    /**Nút thông báo người học */
    let isShowBtnNotice = false;
    /**Nút dừng tính hồ sơ */
    let isShowBtnStopCounting = false;
    /**Nút phối hợp xử lý a.k.a liên thông xin ý kiến */
    let isShowBtnCoordination = false;
    /**Hiển thị nút thu hồi các xử lý liên thông, xin ý kiến,.. */
    let isShowBtnCancelConnect = false;
    /**Nút giao việc */
    let isShowBtnAssignTask = false;
    /**Nút thu hồi giao việc */
    let isShowBtnRevokeAssignTask = false;
    /**Nút trả lời giao việc */
    let isShowBtnReturnAssignTask = false;
    /**Nút duyệt y.c bổ sung hồ sơ */
    let isShowBtnDecisionStopCounting = false;
    /**Nút từ chối y.c bổ sung hồ sơ */
    let isShowBtnCancelStopCounting = false;
    /**Nút chuyển duyệt y.c dừng tính */
    let isShowBtnForwardStopCounting = false;
    /**Nút thay đổi người thụ lý chi tiết hồ sơ */
    let isShowBtnReAssignProcess = false;

    /**Nút tiếp nhận liên thông, xin ý kiến */
    let isShowBtnReceiveConnect = false;
    /**Nút hủy tiếp nhận liên thông */
    let isShowBtnRefuseConnect = false;
    /**Nút trả lời phối hợp xử lý (Xin ý kiến) */
    let isShowBtnReturnCoordination = false;
    /**Nút in quyết định */
    let isShowBtnPrintDecision = false;
    /**Nút duyệt & giao trả hồ sơ */
    let isShowBtnApproveAndReturn = false;
    /**Nút trả kết quả kèm bưu chính */
    let isShowBtnReturnResultWithPostal = false;

    // Lấy thông tin dừng tính
    if (dossier?.DossierPauses?.length) {
        dossierPauses = [...dossier.DossierPauses];
    }

    // Lấy thông tin phí của hồ sơ
    if (dossier?.DossierFees?.length) {
        dossierFees = [...dossier.DossierFees];
    }

    // Kiểm tra thông tin các luồng xử lý hiện tại của hồ sơ
    if (dossier?.DossierSummaryProcesses?.length) {
        dossierSummaryProcess = sortBySendAt([
            ...dossier.DossierSummaryProcesses,
        ]);

        // // Kiểm tra và đưa đơn vị chính của nhân viên lên đầu
        // if (dossierSummaryProcess.length > 1) {
        //     const index = dossierSummaryProcess.findLastIndex(
        //         (obj) => obj.UnitCode === unitCode
        //     );

        //     if (index > -1) {
        //         const [item] = dossierSummaryProcess.splice(index, 1);
        //         dossierSummaryProcess.unshift(item); // Đưa obj đó lên đầu mảng
        //     }
        // }

        // Lấy ra luồng xử lý đúng với đơn vị của nhân viên hiện tại và trạng thái là đang xử lý
        currentSummaryProcess = getCurrentSummaryProcess({
            dossier: dossier,
            data: dossierSummaryProcess,
            unitCode: unitCode,
        });

        // Luồng xử lý chỉnh (Main) của hồ sớ này
        mainSummaryProcess = dossierSummaryProcess.find(
            (x) => x.Type === ForwardType.Main
        );

        //Lấy thông tin node tiếp nhận ban đầu
        initialNodeReceive =
            currentSummaryProcess?.DossierDetailProcesses?.find(
                (x) => x.IsInitial
            );

        if (currentSummaryProcess?.Pause_IsActive) {
            isAdditionalRequest = true;

            // Chỉ cán bộ tiếp nhận ban đầu hồ sơ mới có thể thấy nút tiếp nhận bổ sung
            const isReceiverEpp =
                employeeId === initialNodeReceive?.EmployeeId ||
                employeeId === currentSummaryProcess.Send_EmployeeId;
            if (
                isReceiverEpp &&
                currentSummaryProcess.Pause_Status ===
                SummaryPauseStatus.Additional
            ) {
                isShowBtnReceiveAdditional = true;
            }
        }
    }

    // Lấy ra quy trình đang thực hiện của thủ tục
    if (dossier?.Procedure) {
        const procedure = dossier.Procedure;

        // clone danh sách các bước xử lý ra
        implementProcesses = sortByNumberNullable(
            procedure.ImplementationProcesses ?? [],
            'Ordinal',
            'asc'
        );

        /**Lấy ra quy trình đang xử lý hiện tại của hồ sơ */
        const currentImplementProcess = getImplementProcess({
            procedure,
            implementProcessCode:
                currentSummaryProcess?.Detail?.ImplementationProcessCode ?? implementProcesses.find(x => x.IsProcedureDef)?.Code,
        });

        // Lấy ra các bước thực hiện của quy trình trên
        if (currentImplementProcess?.Code && currentImplementProcess.Code.length) {
            implementProcessCode = currentImplementProcess.Code;
            // Sắp xếp lại theo thứ tự tăng dần các bước
            implementSteps =
                currentImplementProcess.ImplementationSteps &&
                    currentImplementProcess.ImplementationSteps
                        .length
                    ? sortByNumberNullable(
                        currentImplementProcess.ImplementationSteps ?? [],
                        'Ordinal',
                        'asc'
                    )
                    : [];

            if (implementSteps.length) {
                //TODO: Tìm bước đầu và bước cuối của danh sách xử lý
                startImplementStepCode = implementSteps[0].Code; //? Bước đầu
                endImplementStepCode =
                    implementSteps[implementSteps.length - 1].Code; //? Bước đầu
            }
        }
    }

    // Lấy ra bước xử lý hiện tại của nhân viên đang xử lý
    if (currentSummaryProcess?.DossierDetailProcesses?.length && employeeId) {
        // Lấy ra chi tiết xử lý gần nhất của nhân viên và không phải là action Thu hồi
        currentProcessStep = getCurrentDossierDetailProcess({
            data: currentSummaryProcess.DossierDetailProcesses,
            empId: employeeId,
        });

        //Lấy chi tiết xử lý mới nhất của hồ sơ
        latestProcessStep = mergeDetailInto(
            currentSummaryProcess.DossierDetailProcesses.findLast(
                (x) =>
                    x.ProcessingStatus === DossierDetailProcessingStatus.Pending
            )
        );

        // Nếu không tìm thấy chi tiết mới nhất của hồ sơ thì tự động gán phần tử cuối của danh sách xử lý hồ sơ
        latestProcessStep ??= mergeDetailInto(
            currentSummaryProcess.DossierDetailProcesses[
            currentSummaryProcess.DossierDetailProcesses.length - 1
            ]
        );

        // Tìm index của bước xử lý mới nhất trong mảng ImplementSteps
        let currImpStepIdx = implementSteps.findIndex(
            (x) => x.Code === latestProcessStep?.Step_CurrentCode
        );

        if (currImpStepIdx === -1) {
            // Nếu không tìm thấy, thì nó là bước khởi tạo đầu tiên
            currImpStepIdx = 0;
        }

        // Lấy ra bước xử lý hiện tại
        currentImplementStep = implementSteps?.[currImpStepIdx];
        // Lấy ra bước xử lý trước đó
        beforeImplementStep =
            currImpStepIdx > 0
                ? implementSteps?.[currImpStepIdx - 1]
                : undefined;
        // Lấy ra bước xử lý tiếp theo
        nextImplementStep =
            currImpStepIdx <= implementSteps.length - 1
                ? implementSteps?.[currImpStepIdx + 1]
                : implementSteps[0];
    }

    /**
     * `latestProcessStep` được gửi ĐI TỪ bước nào (`Step_BeforeCode`) — nếu
     * bước đó có cấu hình `BusinessCode` (hệ thống bên thứ 3) thì bản ghi này
     * được tạo bởi nút "Chuyển bộ phận chuyên môn" (ForwardOutProcess), KHÔNG
     * phải "Chuyển bước tiếp theo" thường. Xem button-forward-third-party.tsx.
     * Trường hợp này KHÔNG cho thu hồi — hồ sơ/file đã thực sự rời khỏi luồng
     * nội bộ để sang hệ thống ngoài (vd. trình ký EOFFICE), thu hồi phía
     * trong không huỷ được xử lý phía ngoài.
     */
    const isLatestSentToSpecializedDept = !!implementSteps.find(
        (s) => s.Code === latestProcessStep?.Step_BeforeCode
    )?.BusinessCode;

    // Kiểm tra giao việc (AssignTask)
    if (
        checkAssignTask &&
        currentSummaryProcess?.AssignTasks?.length &&
        employeeId
    ) {
        assignTasks = [...currentSummaryProcess.AssignTasks];
        currentAssignTask = mergeDetailInto(
            assignTasks.find(
                (task) =>
                    task.EmployeeId === employeeId &&
                    task.ProcessingStatus === AssignTaskStatus.Pending
            )
        );
        isShowBtnReturnAssignTask = !!currentAssignTask; //? Hiện nút trả lời giao việc nếu nhân viên có thông tin giao việc và đang xử lý
    }

    // Dưới đây kiểm tra các nút---------------------

    /**Nhân viên đang xử lý có phải nhân viên tiếp nhận ban đầu của hồ sơ không? */
    const isTheReceivedEmp =
        mainSummaryProcess?.Send_EmployeeId === employeeId ||
        dossier?.Receiving_EmployeeId === employeeId;

    /**
     * Hồ sơ này đang chờ trả lời kết quả từ liên thông (Bao gồm chờ tiếp nhận và đang xử lý liên thôngthông)
     * Nếu "true", thì hồ sơ này không được xử lý cho đến khi liên thông trả lời
     */
    const summaryWaitReturnCoordination = mergeDetailInto(
        dossier?.DossierSummaryProcesses?.findLast((summary) => {
            return (
                summary.Type === ForwardType.Connect &&
                (summary.Status === DossierSummaryProcessStatus.Waiting ||
                    summary.Status === DossierSummaryProcessStatus.Processing)
            );
        })
    );

    if (
        checkBtnState &&
        dossier?.Status !== DossierStatus.Returned && // Khác trạng thái đã giao trả
        (!isAdditionalRequest || (isAdditionalRequest && isTheReceivedEmp)) //TODO:Nếu đang dừng tính bổ sung thì chỉ có nhân viên tiếp nhận ms có thể xử lý
    ) {
        //Đối với hồ sơ chờ tiếp nhận
        if (!isEdit || isWaitingReceiveOnline) {
            isShowBtnCreateDossier = true;
            isShowBtnReceiveCompleted = true;

            if (isWaitingReceiveOnline) {
                isShowBtnNotice = true;
            }
        }

        // Trường hợp hồ sơ được tiếp nhận hoàn thành
        if (
            dossier?.Status === DossierStatus.WaitingForReturn &&
            dossier.IsFinishWhenRecv &&
            dossier.Receiving_EmployeeId === employeeId
        ) {
            isShowBtnNotice = true;
            isShowBtnPrintReceipt = true;
            isShowBtnReturnResult = true;

            // Kiểm tra thông tin postalInfor
            // Nếu không có thông tin postalInfor thì hiển thị nút trả kết quả kèm bưu chính
            if (
                !dossier.DossierPostalInformations?.length ||
                dossier.DossierPostalInformations.some(
                    (x) => x.Status === DossierPostalInformationStatus.Initial
                )
            ) {
                isShowBtnReturnResultWithPostal = true;
            }
        }

        // Trường hợp hồ sơ bị hủy bởi nhân viên tiếp nhận
        if (
            dossier?.Condition === DossierCondition.Cancel &&
            dossier.Receiving_EmployeeId === employeeId
        ) {
            isShowBtnReturnResult = true;
        }

        // Kiểm tra nếu hồ sơ có thông tin liên thông và được phép thu hồi
        if (summaryWaitReturnCoordination) {
            // Cho phép thu hồi liên thông nếu trạng thái chờ tiếp nhận và người gửi là nhân viên đang xử lý
            if (
                summaryWaitReturnCoordination.Status ===
                DossierSummaryProcessStatus.Waiting &&
                summaryWaitReturnCoordination.Send_EmployeeId === employeeId
            ) {
                isShowBtnCancelConnect = true;
            }
        }

        //Trường hợp luồng xử lý hồ sơ của đơn vị bạn là luồng xử lý chính hoặc tiếp nhận mới
        if (
            (!dossier || currentSummaryProcess?.Type === ForwardType.Main) &&
            !summaryWaitReturnCoordination // Luồng xử lý chính phải đợi bên liên thông trả lời kết quả(Nếu có)
        ) {
            //TODO: xử lý nút trong chi tiét hồ sơ
            // Trường hợp có thông tin hồ sơ
            if (isEdit) {
                isShowBtnPrintReceipt = !isAdditionalRequest; // Luôn hiện nút in biên nhận trừ đang tiếp nhận bổ sung bổ sung

                // Hiển thị nút thông báo đối với trường hợp nhân viên tiếp nhận ban đầu hoặc hồ sơ chờ tiếp nhận online
                if (isTheReceivedEmp) {
                    isShowBtnNotice = true;
                }

                if (
                    !currentSummaryProcess?.Pause_IsActive && //TODO: Không có dừng tính trên luồng này
                    !isWaitingReceiveOnline &&
                    ![
                        DossierStatus.WaitingForReturn,
                        DossierStatus.NotResolve,
                        DossierStatus.Returned,
                    ].includes(dossier?.Status!) &&
                    dossier?.Condition !== DossierCondition.Cancel
                ) {
                    const receiveDetailProcess =
                        currentSummaryProcess?.DossierDetailProcesses?.find(
                            (x) => x.IsInitial
                        );

                    // Nhân viên đang xử lý là nhân viên tiếp nhận hồ sơ này thì mới có quyền sửa, hủy
                    if (
                        receiveDetailProcess &&
                        receiveDetailProcess.EmployeeId === employeeId
                    ) {
                        isShowBtnCancelDossier = true;

                        if (
                            !isWaitingReceiveOnline &&
                            dossier?.Status! < DossierStatus.WaitingForReturn
                        ) {
                            isShowBtnEdit = true;
                        }
                    }
                }
            }

            //TODO: xử lý nút trong chi tiết xử lý hồ sơ
            //Không xử lý khi đang dừng tính
            if (!isAdditionalRequest) {
                // Trường hợp xử lý mới nhất của hồ sơ hiện tại là do nhân viên đang đăng nhập thực hiện
                if (
                    latestProcessStep?.EmployeeId ===
                    currentProcessStep?.EmployeeId
                ) {
                    if (dossier?.Status === DossierStatus.WaitingForReturn) {
                        isShowBtnReturnResult = true;
                        if (
                            !dossier.DossierPostalInformations?.length ||
                            dossier.DossierPostalInformations.some(
                                (x) =>
                                    x.Status ===
                                    DossierPostalInformationStatus.Initial
                            )
                        ) {
                            isShowBtnReturnResultWithPostal = true;
                        }
                    } else {
                        // Kiểm tra bước xử lý hiện tại là bước đầu tiên (Tiếp nhận hồ sơ)
                        if (
                            currentProcessStep?.IsInitial ||
                            startImplementStepCode ===
                            currentImplementStep?.Code
                        ) {
                            isShowBtnForwardNext = true;
                        } else {
                            // Kiểm tra bước xử lý hiện tại không phải bước đầu tiên và cũng không phải là bước cuối
                            if (!currentProcessStep?.IsFinal) {
                                isShowBtnForwardPrev = true;
                                isShowBtnForwardNext = true;
                                isShowBtnStopCounting = true;
                                isShowBtnAssignTask = true;
                                isShowBtnApproveDossier = true;
                                isShowBtnApproveAndReturn = true;

                                /**
                                 * Kiểm tra nhân viên này có gửi giao việc cho ai không
                                 * Và người đó thực hiện việc này xong chưa
                                 * Nếu chưa thì hiện nút thu hồi giao việc
                                 */
                                const hvAssignTaskSendedUnfinished =
                                    assignTasks.some((x) => {
                                        return (
                                            x.Send_EmployeeId === employeeId &&
                                            x.ProcessingStatus ===
                                            AssignTaskStatus.Pending
                                        );
                                    });

                                if (hvAssignTaskSendedUnfinished) {
                                    isShowBtnRevokeAssignTask = true;
                                }

                                if (
                                    currentImplementStep
                                        ?.ExternalImplementationProcessCodes
                                        ?.length
                                ) {
                                    // Bước này có tồn tài thông tin liên thông/xin ý kiến
                                    isShowBtnCoordination = true;
                                }
                            }
                        }
                    }
                }

                // Trường hợp xử lý mới nhất của hồ sơ hiện tại là công việc do nhân viên đang đăng nhập tạo
                // Và trạng thái xử lý của hồ sơ đang là Pending và không có yêu cầu dừng tính

                if (
                    !latestProcessStep?.IsInitial &&
                    latestProcessStep?.Send_EmployeeId ===
                    currentProcessStep?.EmployeeId &&
                    latestProcessStep?.ProcessingStatus !==
                    DossierDetailProcessingStatus.Revoke &&
                    !latestProcessStep?.IsDossierPause &&
                    //
                    dossier?.Condition !== DossierCondition.Cancel
                ) {
                    /**
                     * Kiểm tra xem bước xử lý được gửi đã có thông tin xử lý chưa?
                     * Nếu đã được xử lý rồi thì không đươc thu hồi
                     */
                    const hasSummaryProcessing = dossierSummaryProcess.some(
                        (x) =>
                            x.Type !== ForwardType.Main &&
                            x.Detail_ParentCode === latestProcessStep?.Code
                    );

                    /**
                     * Kiểm tra xem bước xử lý được gửi đã có thông tin xử lý chưa?
                     * Nếu đã được xử lý rồi thì không đươc thu hồi
                     * Trách việc thu hồi liên tục nhiều lần
                     */
                    const hasDetailProcessing =
                        currentSummaryProcess?.DossierDetailProcesses?.some(
                            (x, idx, details) => {
                                // Bước xử lý hiện tại là cha của bước khác
                                const hvParentProcess =
                                    latestProcessStep?.Code === x.ParentCode;

                                return hvParentProcess;
                            }
                        );

                    /**
                     * Kiểm tra xem bước xử lý được gửi đã có thông tin xử lý chưa?
                     * Nếu đã được xử lý rồi thì không đươc thu hồi
                     */
                    const hasAssignTask = assignTasks.some((x) => {
                        x.DossierDetailProcessCode === latestProcessStep?.Code;
                    });

                    // /**Tự chuyển bản thân thì ẩn nút thu hồi */
                    // const isMeForward =
                    //     currentProcessStep?.Code === latestProcessStep?.Code;

                    /**
                     * Là đã có thông tin xử lý như
                     * - Gửi liên thông/xin ý kiến
                     * - Gửi giao việc
                     * ==> KHông thể thu hồi được vì đã có xử lý công việc
                     */
                    const hvHandler =
                        hasSummaryProcessing ||
                        hasDetailProcessing ||
                        hasAssignTask;

                    // /**
                    //  * Đây là hồ sơ bị duyệt/từ chối xử lý
                    //  */
                    // const isHandleApproval =
                    //     dossier?.Condition === DossierCondition.NotResolved ||
                    //     latestProcessStep?.Send_Action ===
                    //     DossierButtonAction.EMP_ApproveDossier;

                    if (
                        !hvHandler &&
                        !latestProcessStep?._isAdditional && // Không phải nút tiếp nhận bổ sung
                        !isLatestSentToSpecializedDept // Đã gửi Bộ phận chuyên môn thì không cho thu hồi
                    ) {
                        isShowBtnRevoke = true;
                        isShowBtnReAssignProcess = true;
                    }
                }

                // Kiểm tra có thông tin liên thông không?
                // Và đơn vị nhân viên thông đã tiếp nhận chưa?
                // Và người gửi có phải là nhân viên đang xử lý hay không?
                // Nếu chưa thì hiển thị nút thu hồi cho phép nhân viên thu hồi lại thao tác xử lý
                const checkConnectProcess = dossierSummaryProcess.some((x) => {
                    return (
                        (x.Type === ForwardType.Connect ||
                            x.Type === ForwardType.AskAdvice) &&
                        x.Status === DossierSummaryProcessStatus.Waiting &&
                        x.Send_EmployeeId === employeeId
                    );
                });

                if (checkConnectProcess) {
                    isShowBtnCancelConnect = true;
                }
            }
        }

        //? Đối với trường hợp luồng xử lý của nhân viên hiện tại là phối hợp xử lý (Liên thông hoặc xin ý kiến)
        if (
            currentSummaryProcess?.Type === ForwardType.AskAdvice ||
            currentSummaryProcess?.Type === ForwardType.Connect
        ) {
            isReadOnly = true; //TODO: Đối với luồng xử lý khác luồng xử lý chính, chỉ cho phép đọc

            // Hiển thị nút tiếp nhận liên thông
            if (
                currentSummaryProcess.Status ===
                DossierSummaryProcessStatus.Waiting
            ) {
                isShowBtnReceiveConnect = true;
                isShowBtnRefuseConnect = true;
            }

            // Trường hợp xử lý mới nhất của hồ sơ hiện tại là do nhân viên đang đăng nhập thực hiện
            if (
                latestProcessStep?.EmployeeId === currentProcessStep?.EmployeeId
            ) {
                if (
                    dossier?.Status !== DossierStatus.WaitingForReturn &&
                    currentSummaryProcess.Status !==
                    DossierSummaryProcessStatus.Fininshed
                ) {
                    // Kiểm tra bước xử lý hiện tại là bước đầu tiên (Tiếp nhận hồ sơ)
                    if (
                        currentProcessStep?.IsInitial ||
                        startImplementStepCode === currentImplementStep?.Code
                    ) {
                        isShowBtnForwardNext = true;
                    } else {
                        // Kiểm tra bước xử lý hiện tại không phải bước đầu tiên và cũng không phải là bước cuối
                        const hvBeforeAndAfterStep = !!(
                            nextImplementStep && beforeImplementStep
                        );

                        if (
                            !currentProcessStep?.IsFinal &&
                            hvBeforeAndAfterStep
                        ) {
                            isShowBtnForwardPrev = true;
                            isShowBtnForwardNext = true;
                            isShowBtnAssignTask = true;
                            isShowBtnApproveDossier = true;
                            isShowBtnApproveAndReturn = true;
                        }
                    }
                }
            }

            // Trường hợp xử lý mới nhất của hồ sơ hiện tại là công việc do nhân viên đang đăng nhập tạo
            // Và trạng thái xử lý của hồ sơ đang là Pending
            if (
                !latestProcessStep?.IsInitial &&
                latestProcessStep?.Send_EmployeeId ===
                currentProcessStep?.EmployeeId &&
                latestProcessStep?.ProcessingStatus ===
                DossierDetailProcessingStatus.Pending
            ) {
                /**
                 * Kiểm tra xem bước xử lý được gửi đã có thông tin xử lý chưa?
                 * Nếu đã được xử lý rồi thì không đươc thu hồi
                 * Trách việc thu hồi liên tục nhiều lần
                 */
                const hasDetailProcessing =
                    currentSummaryProcess?.DossierDetailProcesses?.some(
                        (x, idx, details) => {
                            // Bước xử lý hiện tại là cha của bước khác
                            const hvParentProcess =
                                latestProcessStep?.Code === x.ParentCode;

                            return hvParentProcess;
                        }
                    );

                /**
                 * Kiểm tra xem bước xử lý được gửi đã có thông tin xử lý chưa?
                 * Nếu đã được xử lý rồi thì không đươc thu hồi
                 */
                const hasAssignTask = assignTasks.some((x) => {
                    x.DossierDetailProcessCode === latestProcessStep?.Code;
                });

                // /**Tự chuyển bản thân thì ẩn nút thu hồi */
                // const isMeForward =
                //     currentProcessStep?.Code === latestProcessStep?.Code;

                // /**
                //  * Đây là hồ sơ bị trưởng phòng duyệt/từ chối xử lý
                //  */
                // const isHandleApproval =
                //     latestProcessStep?.Send_Action ===
                //         DossierButtonAction.EMP_RefuseDossier ||
                //     latestProcessStep?.Send_Action ===
                //         DossierButtonAction.EMP_ApproveDossier;

                const hvHandlerInfo = hasDetailProcessing || hasAssignTask;

                if (!hvHandlerInfo && !isLatestSentToSpecializedDept) {
                    isShowBtnRevoke = true;
                    isShowBtnReAssignProcess = true;
                }
            }

            // TODO: Luôn hiển thị nút trả két quả nếu xử lý mới nhất là xử lý của nhân viên hiện tại
            if (
                currentSummaryProcess.Status ===
                DossierSummaryProcessStatus.Processing &&
                currentProcessStep?.Code === latestProcessStep?.Code
            ) {
                isShowBtnReturnCoordination = true;
            }
        }
    }

    /**
     * BusinessCode (hệ thống bên thứ 3) cấu hình sẵn của bước ĐANG xử lý —
     * cùng cách tra như `currentStepDef` trong button-forward-third-party.tsx.
     * Dùng để quyết định có hiện nút "Chuyển bộ phận chuyên môn" hay không.
     */
    const currentStepBusinessCode = implementSteps.find(
        (s) => s.Code === currentProcessStep?.Step_CurrentCode
    )?.BusinessCode;

    return {
        /**Danh sách các đơn vị xử lý */
        dossierSummaryProcesses: dossierSummaryProcess,
        /**Cây công việc đã thực hiện */
        implementSteps,
        /**Danh sách các quy trình xử lý */
        implementProcesses,

        /**Code của bước xử lý đầu tiên */
        startImplementStepCode,
        /**Code của bước xử lý cuối cùng */
        endImplementStepCode,

        /**Mã quy trình xử lý hiện tại */
        implementProcessCode,
        /**Đây là luồng xử lý chính được lấy theo đơn vị */
        currentSummaryProcess,
        /**Thông tin xử lý mới nhất của nhân viên đang đăng nhập */
        currentProcessStep,
        /**Thông tin xử lý mới nhất của hồ sơ */
        latestProcessStep,
        /**Bước xử lý hiện tại của hồ sơ */
        currentImplementStep,
        /**Bước xử lý trước bước hiện tại của hồ sơ */
        beforeImplementStep,
        /**Bước xử lý sau bước hiện tại của hồ sơ */
        nextImplementStep,
        /**Danh sách giao việc */
        assignTasks: assignTasks,
        /**Chi tiết giao việc hiện tại của nhân viên đang xử lý (Nếu có) */
        currentAssignTask: currentAssignTask,
        /**Danh sách dừng tính hồ sơ */
        dossierPauses,
        /**Danh sách phí của hồ sơ */
        dossierFees,
        /**Thông tin động của hồ sơ */
        dynamicInfo: parseDynamicFormConfigData(dossier?.ExtendedInfoJson),

        // Nút
        /**Chờ tiếp nhận */
        isWaitingReceiveOnline,
        /**Trạng thái chỉnh sửa */
        isEdit,
        /**Trạng thái chỉ xem hồ sơ */
        isReadOnly: isReadOnly,
        /**Đang có yêu cầu bổ sung hồ sơ */
        isAdditionalRequest,

        /**Có phải nhân viên tiếp nhận ban đầu của hồ sơ */
        isReceptionist: isTheReceivedEmp,

        // TRANG THAI CAC NUT
        btnState: {
            [DossierButtonAction.EMP_Creates]: isShowBtnCreateDossier,
            [DossierButtonAction.EMP_ReceiveCompleted]:
                isShowBtnReceiveCompleted,
            [DossierButtonAction.EMP_ReceiveAdditional]:
                isShowBtnReceiveAdditional,
            [DossierButtonAction.EMP_Edit]: isShowBtnEdit,
            [DossierButtonAction.EMP_PrintReceipt]: isShowBtnPrintReceipt,
            [DossierButtonAction.EMP_Cancel]: isShowBtnCancelDossier,

            // Bước ĐANG xử lý có cấu hình BusinessCode (hệ thống bên thứ 3) thì
            // CHỈ hiện "Chuyển bộ phận chuyên môn", ẩn "Chuyển bước tiếp theo"
            // — 2 nút loại trừ nhau để tránh cán bộ chuyển tay khi bước này
            // phải đi qua hệ thống ngoài.
            [DossierButtonAction.EMP_Forward]:
                isShowBtnForwardNext && !currentStepBusinessCode,
            // Xem [[ThirdPartySystemBusinessCode]] và button-forward-third-party.tsx
            [DossierButtonAction.EMP_ThirdPartySystem]:
                isShowBtnForwardNext && !!currentStepBusinessCode, 
            [DossierButtonAction.EMP_PrevForward]: isShowBtnForwardPrev,
            [DossierButtonAction.EMP_Coordination]: isShowBtnCoordination,
            [DossierButtonAction.EMP_ReturnResult]: isShowBtnReturnResult,
            [DossierButtonAction.EMP_ReturnResultWithPostal]:
                isShowBtnReturnResultWithPostal, // TODO: Trả kết quả có gửi kèm theo bưu phẩm thì hiển thị cùng với trả kết quả bình thường
            [DossierButtonAction.EMP_Revoke]: isShowBtnRevoke,
            [DossierButtonAction.EMP_ApproveDossier]: isShowBtnApproveDossier,
            [DossierButtonAction.EMP_SendNotice]: isShowBtnNotice,
            [DossierButtonAction.EMP_StopDossierToCouting]: isShowBtnStopCounting,
            [DossierButtonAction.EMP_AssignTask]: isShowBtnAssignTask,
            [DossierButtonAction.EMP_RevokeAssignTask]: isShowBtnRevokeAssignTask,
            [DossierButtonAction.EMP_ReturnAssignTask]: isShowBtnReturnAssignTask,
            [DossierButtonAction.EMP_ReceiveConnect]: isShowBtnReceiveConnect,
            [DossierButtonAction.EMP_CancelCoordination]: isShowBtnCancelConnect,
            [DossierButtonAction.EMP_RefuseConnect]: isShowBtnRefuseConnect,
            [DossierButtonAction.EMP_ReturnCoordination]: isShowBtnReturnCoordination,
            [DossierButtonAction.EMP_DecisionStopCounting]: isShowBtnDecisionStopCounting,
            [DossierButtonAction.EMP_RefuseStopCounting]: isShowBtnCancelStopCounting,
            [DossierButtonAction.EMP_ForwardRequestStopCounting]: isShowBtnForwardStopCounting,
            [DossierButtonAction.EMP_FinishAndReturn]: isShowBtnApproveAndReturn,
            // [DossierButtonAction.EMP_ReAssignProcess]: isShowBtnReAssignProcess,
            [DossierButtonAction.EMP_PrintDecision]: isShowBtnApproveDossier, // TODO: Duyệt hồ sơ được là in quyết định được
            [DossierButtonAction.EMP_CancelStopCounting]: false, //TODO: luôn false khi khởi tạo
            [DossierButtonAction.EMP_ContinueHandle]: false, //TODO: luôn false khi khởi tạo
        },

        /**Đối tượng hồ sơ */
        dossierItem: dossier,

        //TODO: Phía dưới là ghi đè các giá trị khi khởi tạo
        isHandleAfterReceive: false,
        isFinishReceive: false,
        createdDossierIds: [],
    } as Partial<DossierStoreProps>;
}

/**
 * Hàm lấy ra đúng luồng xử lý(summary) của nhân viên đang đăng nhập
 * Summary phải khác trạng thái hủy(Cancel) và từ chối(Refuse)
 */
function getCurrentSummaryProcess({
    dossier,
    data = [],
    unitCode,
}: {
    dossier?: DossierDetailResponse;
    data?: DossierSummaryProcessesResponse[];
    unitCode?: string;

    /** Lấy luôn cả những summary đã hoàn thành  */
    includeFinished?: boolean;
}): DossierSummaryProcessesResponse | undefined {
    let current = data.findLast(
        (x) =>
            x.UnitCode === unitCode &&
            x.Status !== DossierSummaryProcessStatus.Cancel &&
            x.Status !== DossierSummaryProcessStatus.Refuse //TODO: Có vấn đề khi lấy hoàn thành hoặc không hoàn thành
    );

    // Check một số điều kiện khác nếu có truyền dossier
    if (dossier) {
        // Nếu hồ sơ đã bị hủy thì lấy luồng xử lý không cần phân biệt trạng thái
        if (dossier.Condition === DossierCondition.Cancel) {
            current = data.findLast(
                (x) =>
                    x.UnitCode === unitCode &&
                    x.Status !== DossierSummaryProcessStatus.Refuse &&
                    x.Status !== DossierSummaryProcessStatus.Fininshed
            );
        }
    }

    if (current?.DossierDetailProcesses?.length) {
        const { dossierDetailProcesses, hvPauseDataCode } =
            handlePauseDetailData(current.DossierDetailProcesses);

        current._arrDetailProcessCodeHasPauseInfo = hvPauseDataCode; // Lưu cấc dossierDetailProcessingCode có thông tin dừng tính
        // Sort lại các DossierDetailProcessing theo đúng thứ tự thực hiện
        current.DossierDetailProcesses = sortBySendAt(
            dossierDetailProcesses?.map(mergeDetailInto)
        );
    }
    return mergeDetailInto(current);
}

/**
 * Lấy ra chi tiết xử lý công việc hiện tại của nhân viên
 * Lấy ra chi tiết xử lý gần nhất của nhân viên và không phải là action Thu hồi
 */
function getCurrentDossierDetailProcess({
    data,
    empId,
}: {
    data?: DossierDetailProcessResponse[];
    empId?: string;
}) {
    const current = (data ?? []).findLast(
        (x) =>
            x.EmployeeId === empId &&
            x.ProcessingStatus !== DossierDetailProcessingStatus.Revoke
    );

    return mergeDetailInto(current);
}

/**
 * Đối với 1 số luồng xử lý có thông tin dừng tính đã hoàn thành, thì xử lý dữ liệu đó để hiển thị UI cho hợp lý
 */
function handlePauseDetailData(
    dossierDetailProcesces: DossierDetailProcessResponse[]
) {
    const cloneData: DossierDetailProcessResponse[] = [
        ...dossierDetailProcesces,
    ];
    /**Mảng các dossierPauseDetailCode có thông tin dừng tính */
    const hvPauseDataCode: string[] = [];

    for (let x of cloneData) {
        if (x.IsDossierPause) {
            // Gán các code của chi tiết xử lý có thông tin dừng tính vô trường này
            hvPauseDataCode.push(x.Code);

            // Nếu có thông tin dừng tính ==> Kiểm tra xem có chi tiét xử lý tiếp nhận dừng tính bổ sung không
            // const findIdx = cloneData.findIndex((z) => z.ParentId === x.ID);

            // if (findIdx != -1) {
            //     // Nếu có node tiếp nhận bổ sung
            //     const prevData = cloneData[findIdx];

            //     // Chỉnh sửa data của node đó cho hợp lý trên UI
            //     cloneData[findIdx] = {
            //         ...prevData,
            //         Send_EmployeeId: prevData.Send_EmployeeId,
            //         EmployeeId: x.EmployeeId,
            //         _isAdditional: true,
            //     };
            // }
        }
    }
    return {
        dossierDetailProcesses: cloneData,
        hvPauseDataCode,
    };
}

/**Lấy ra quy trình xử lý đúng với luồng xử lý hiện tại */
function getImplementProcess({
    procedure,
    implementProcessCode,
}: {
    implementProcessCode?: string;
    procedure: ProcedureResponse;
}): ImplementationProcess | undefined {
    return procedure.ImplementationProcesses?.find(
        (x) => x.Code === implementProcessCode
    );
}

/**Hàm dùng để kiểm tra trạng thái các nút khi hồ sơ có yêu cầu dừng tính hoặc đã bị dừng tính */
function checkDossierStopCounting({
    dossierItem,
    employeeId,
    stopCounting,
}: {
    dossierItem?: DossierDetailResponse;
    employeeId?: string;
    stopCounting: DossierPauseResponse;
}) {
    let isShowBtnDecision = false;
    let isShowBtnCancel = false;
    let isShowBtnContinue = false;
    let isShowBtnForward = false;

    if (dossierItem && employeeId) {
        /**Đây là yêu cầu dừng tính bổ sung hồ sơ */
        const isRequestStopCounting =
            stopCounting?.Status === DossierPauseStatus.Additional;

        /**Đây là hồ sơ dừng tính tại chỗ */
        const isStopCountingByOther =
            stopCounting?.Status === DossierPauseStatus.Other;

        if (
            isRequestStopCounting &&
            stopCounting.Type === DossierPauseType.Request
        ) {
            // Đây là điều kiện check thằng trưởng phòng có chuyển duyệt yêu cầu bổ sung không
            const hvForwardRequestStopCounting =
                checkForwardDecisionStopCounting({
                    stopCounting,
                });

            if (!!hvForwardRequestStopCounting) {
                isShowBtnDecision = true;
            } else {
                if (stopCounting.RequestBy_EmployeeId === employeeId) {
                    isShowBtnCancel = true;
                }

                if (
                    stopCounting.DecidedBy_EmployeeId === employeeId ||
                    stopCounting.OtherRelated_EmployeeId === employeeId
                ) {
                    isShowBtnDecision = true;

                    // Người nhận xử lý chính có quyền chuyển y.c duyệt sang cho người khác
                    if (stopCounting.DecidedBy_EmployeeId === employeeId) {
                        isShowBtnForward = true;
                    }
                }
            }
        }

        if (
            isStopCountingByOther &&
            stopCounting.RequestBy_EmployeeId === employeeId
        ) {
            isShowBtnContinue = true;
        }
    }

    return {
        /**Nút duyệt/từ chối yêu cầu bổ sung */
        isShowBtnDecision,
        /**Nút cancel yêu cầu bổ sung */
        isShowBtnCancel,
        /**Nút từ tiếp xử lý (Đối với trường hợp đã dừng tính lý do khác) */
        isShowBtnContinue,
        /**Nút chuyển duyệt y.c dừng tính hồ sơ */
        isShowBtnForward,
    };
}

/**
 * Hàm kiểm tra có thông tin chuyển quyền duyệt y.c dừng tính trên xử lý dừng tính không
 * - ĐK1: Nhân viên gửi là nhân viên có quyền quyết định duyệt/từ chối y.c dừng tính
 * - ĐK2: PauseType là REquest bổ sung
 * - ĐK3: Nhân viên được chuyển quyền quyết định không phải là người liên quan khác(Nhân viên tiếp nhận bổ sung)
 */
function checkForwardDecisionStopCounting({
    stopCounting,
}: {
    stopCounting: DossierPauseResponse;
}) {
    /**Danh sách xử lý chi tiết của dừng tínhtính */
    const arrPauseDetails = sortBySendAt(
        stopCounting?.DossierPauseDetails ?? []
    ).map(mergeDetailInto);

    /**Nhân viên được chuyển quyền quýt định */
    const nodeForwardDecideEmp = arrPauseDetails.findLast((x) => {
        return (
            x?.Send_EmployeeId === stopCounting.DecidedBy_EmployeeId &&
            x?.PauseType === DossierPauseType.Request &&
            x?.EmployeeId !== stopCounting.OtherRelated_EmployeeId
        );
    });

    return nodeForwardDecideEmp;
}

/**
 * Hàm kiểm tra chi tiết xử lý này có phải là node tiếp nhận bổ sung hồ sơ
 * - ĐK1: Đây là nhân viên tiếp nhận hồ sơ
 * - ĐK2: Tìm ra note có yêu cầu bổ sung hồ sơ và đã hoàn thành
 *
 */
function checkAdditonalDossierNode({
    current,
    summaryProcess,
}: {
    current?: DossierDetailProcessResponse;
    summaryProcess?: DossierSummaryProcessesResponse;
}) {
    if (!current || !summaryProcess) return false;
    /**
     * Đây là chi tiết xử lý tiếp nhận bổ sung
     * - ĐK1: Đây là nhân viên tiếp nhận hồ sơ
     * - ĐK2: Tìm ra note có yêu cầu bổ sung hồ sơ và đã hoàn thành
     *  */
    return (
        current?.Send_EmployeeId === summaryProcess?.Send_EmployeeId &&
        summaryProcess.DossierDetailProcesses?.some((x) => {
            return (
                current?.Step_CurrentCode === x.Step_CurrentCode &&
                x.IsDossierPause &&
                x.EmployeeId === current?.EmployeeId
            );
        })
    );
}

/**
 * Hàm sắp xếp lại mảng bằng trường Send_At
 * @default ASC
 *  */
function sortBySendAt<T extends { [key: string]: any } = any>(
    items: T[],
    customKey = 'Send_At'
): T[] {
    // Tạo một map với key là parentCode và value là danh sách các items
    return [...(items ?? [])].sort((a, b) => {
        const defRule =
            new Date(a[customKey]).getTime() - new Date(b[customKey]).getTime();

        return defRule;
    });
}

/**
 * Hàm lấy ra danh sách thông báo được gửi từ cán bộ một cửa cho công dân
 * */
function collectNoticeCustDossier(data?: DossierNotificationResponse | DossierNotificationResponse[] | null) {
    if (!data) return [];

    if (Array.isArray(data)) {
        return data.filter((x) => {
            const isTriggerByEmp =
                x.ActionTriggerName === 'User_Infos' &&
                x.ActionTriggerTrace === DossierButtonAction.EMP_SendNotice;

            return isTriggerByEmp && x.Customer_IsVisible ? x : undefined;
        });
    }

    const isTriggerByEmp =
        data.ActionTriggerName === 'User_Infos' &&
        data.ActionTriggerTrace === DossierButtonAction.EMP_SendNotice;

    return isTriggerByEmp && data.Customer_IsVisible ? [data] : [];
}

/**
 * Merge object detail vào
 * @param data
 * @returns
 */
function mergeDetailInto<T extends {}>(data?: T): T {
    if (!data) return data as T;
    return deepmerge((data as any)?.Detail ?? {}, data);
}

/**
 * Hàm lấy ra bước xử lý mới nhất của hồ sơ
 * - Chỉ lấy bước xử lý mới nhất của hồ sơ, không lấy các bước xử lý đã hoàn thành
 */
function getLatestImplementStep({
    implementationProcesses,
    dossierSummaryProcesses,
    currentSummaryCode,
}: {
    dossierSummaryProcesses?: DossierSummaryProcessesResponse[];
    implementationProcesses?: ImplementationProcess[];
    currentSummaryCode?: string; // Chỉ định đúng summary muốn lấy. nếu không có sẽ lấy summary mặc định (FOrwardType = Main)
}) {
    const currentSummaryProcess = mergeDetailInto(
        dossierSummaryProcesses?.findLast((x) => {
            if (currentSummaryCode) return x.Code === currentSummaryCode;
            return x.Type === ForwardType.Main;
        })
    );

    const dossierDetailProcess =
        currentSummaryProcess?.DossierDetailProcesses ?? [];

    const implementSteps =
        implementationProcesses?.find(
            (x) => x.Code === currentSummaryProcess?.ImplementationProcessCode
        )?.ImplementationSteps;

    const latestDossierDetailProcess = dossierDetailProcess.findLast(
        (x) => x.ProcessingStatus === DossierDetailProcessingStatus.Pending
    );

    let latestImplementStep = implementSteps?.find((x) => {
        return (
            x.Code ===
            mergeDetailInto(latestDossierDetailProcess)?.Step_CurrentCode
        );
    });

    if (!latestImplementStep && implementSteps && implementSteps.length && latestDossierDetailProcess?.IsInitial) {
        latestImplementStep = sortByNumberNullable(implementSteps, 'Ordinal', 'asc')[0];
    }

    return latestImplementStep;
}

/**
 * Hồ sơ có phải do CÁN BỘ tự nộp (qua cổng khách hàng) hay không — đọc thẳng
 * từ `Customer_ExtendedJSON` (ưu tiên bản `...Modified` nếu có) đã có sẵn
 * trên response hồ sơ, KHÔNG cần gọi thêm API. Nhận biết qua key
 * `staff_userId`/`staff_userName` — được ghi vào lúc nộp khi người nộp là
 * cán bộ (xem `dossier-cust-info.tsx`, `mapping.ts` phía MC dùng cùng 2 key
 * này để tính `isStaffSubmitDossier` cho form sửa hồ sơ).
 *
 * Dùng để phân biệt SV/CB khi cần gửi kèm API (vd. in biên nhận) mà không có
 * field nào khác đủ tin cậy (`StudentID` chỉ chắc chắn khi CÓ giá trị, không
 * suy ngược được "rỗng = Cán bộ" vì còn khách vãng lai/tổ chức).
 */
function isStaffSubmittedDossier(dossier?: {
    Customer_ExtendedJSON?: string | null;
    Customer_ExtendedJSONModified?: string | null;
}): boolean {
    const json =
        dossier?.Customer_ExtendedJSONModified || dossier?.Customer_ExtendedJSON;
    if (!json) return false;

    try {
        const parsed = JSON.parse(json) as Record<string, unknown>;
        return !!parsed.staff_userId || !!parsed.staff_userName;
    } catch (error) {
        console.error(
            '[isStaffSubmittedDossier] Fail to parse Customer_ExtendedJSON:',
            error
        );
        return false;
    }
}

export {
    /** */
    checkAdditonalDossierNode,
    /** */
    checkDossierStopCounting,
    /** */
    checkForwardDecisionStopCounting,
    /** */
    collectNoticeCustDossier,
    /** */
    dossierDetailHandler,
    /** */
    getCurrentDossierDetailProcess,
    /** */
    getCurrentSummaryProcess,
    /** */
    getLatestImplementStep,
    /** */
    handlePauseDetailData,
    /** */
    isStaffSubmittedDossier,
    /** */
    mergeDetailInto,
    /** */
    sortBySendAt,
};
